import { Box, Button, Spinner, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { useWizardContext } from "../../context/WizardContext";
import { WIZARD_STEPS } from "../../urls";
import { StepIcon } from "../WizardLayout/StepIcon";
import { messages } from "./messages";

interface StepWrapperProps {
  title: string;
  description?: string;
  skippable?: boolean;
  isLastStep?: boolean;
  isSaving?: boolean;
  formId: string;
  children: React.ReactNode;
}

export const StepWrapper: React.FC<StepWrapperProps> = ({
  title,
  description,
  skippable = false,
  isLastStep = false,
  isSaving = false,
  formId,
  children,
}) => {
  const intl = useIntl();
  const { currentStepId, goToPreviousStep, skipCurrentStep, completeWizard } = useWizardContext();
  // const [isResetting, setIsResetting] = useState(false);
  const currentIndex = WIZARD_STEPS.findIndex(s => s.id === currentStepId);
  const isFirstStep = currentIndex === 0;
  const currentStep = WIZARD_STEPS[currentIndex];
  const totalSteps = WIZARD_STEPS.length;
  const progressPercent = (currentIndex / (totalSteps - 1)) * 100;

  const handleSkip = () => {
    if (isLastStep) {
      completeWizard();
    } else {
      skipCurrentStep();
    }
  };

  // const handleMagicReset = async () => {
  //   setIsResetting(true);

  //   try {
  //     await resetWizardState();
  //   } finally {
  //     setIsResetting(false);
  //   }
  // };

  return (
    <Box
      display="flex"
      flexDirection="column"
      __flex={1}
      __minHeight={0}
      borderRadius={5}
      __backgroundColor="default1"
      style={{
        border: "1px solid rgba(148, 163, 184, 0.18)",
        boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)",
      }}
    >
      <Box
        __flexShrink={0}
        style={{
          paddingLeft: "16px",
          paddingRight: "16px",
          paddingTop: "16px",
          paddingBottom: "16px",
          borderBottom: "1px solid rgba(148, 163, 184, 0.14)",
        }}
      >
        <Box
          display="flex"
          alignItems={{ mobile: "flex-start", tablet: "center" }}
          justifyContent="space-between"
          flexDirection={{ mobile: "column", tablet: "row" }}
          gap={3}
        >
          <Box>
            <Text
              size={1}
              fontWeight="bold"
              color="default2"
              __textTransform="uppercase"
              __letterSpacing="0.08em"
            >
              {intl.formatMessage(messages.stepOf, {
                current: currentIndex + 1,
                total: totalSteps,
              })}
            </Text>
            <Box display="flex" alignItems="center" gap={2} marginTop={2}>
              {currentStep && <StepIcon icon={currentStep.icon} state="current" size="small" />}
              <Box>
                <Text size={4} fontWeight="bold" as="h1">
                  {title}
                </Text>
                {description && (
                  <Text size={3} color="default2" as="p">
                    {description}
                  </Text>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          width="100%"
          __height="4px"
          __backgroundColor="rgba(148, 163, 184, 0.14)"
          __borderRadius="999px"
          __overflow="hidden"
          marginTop={4}
        >
          <Box
            __height="100%"
            __width={`${progressPercent}%`}
            __backgroundColor="#3b82f6"
            __borderRadius="999px"
            __transition="width 0.4s ease"
          />
        </Box>
      </Box>

      <Box
        __flex={1}
        __minHeight={0}
        overflow="auto"
        style={{
          paddingLeft: "16px",
          paddingRight: "16px",
          paddingTop: "16px",
          paddingBottom: "16px",
        }}
      >
        {children}
      </Box>

      <Box
        __flexShrink={0}
        paddingY={4}
        style={{
          paddingLeft: "16px",
          paddingRight: "16px",
          borderTop: "1px solid rgba(148, 163, 184, 0.14)",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ mobile: "stretch", tablet: "center" }}
          flexDirection={{ mobile: "column", tablet: "row" }}
          gap={3}
        >
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Button
              variant="secondary"
              onClick={goToPreviousStep}
              disabled={isFirstStep || isSaving}
            >
              {intl.formatMessage(messages.back)}
            </Button>
            {/*<Button
              variant="tertiary"
              onClick={handleMagicReset}
              disabled={isSaving || isResetting}
            >
              {isResetting ? <Spinner /> : intl.formatMessage(messages.magicReset)}
            </Button>*/}
          </Box>

          <Box
            display="flex"
            gap={2}
            alignItems="center"
            justifyContent={{ mobile: "stretch", tablet: "flex-end" }}
            flexWrap="wrap"
          >
            {skippable && (
              <Button variant="tertiary" onClick={handleSkip} disabled={isSaving}>
                {intl.formatMessage(messages.skipForNow)}
              </Button>
            )}
            <Button
              variant="primary"
              type="submit"
              form={formId}
              disabled={isSaving}
              data-test-id="wizard-next-button"
            >
              {isSaving ? (
                <Spinner />
              ) : isLastStep ? (
                intl.formatMessage(messages.finishSetup)
              ) : (
                intl.formatMessage(messages.saveAndContinue)
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
